/** `GarmentRegion` (garment `region` / `cuts`, hair `grow.region` / zones) and its sphere / box / capsule lists. */

import { arr, ListEditor, Num, num, obj, OptionalSection, patch, PickList, Section, vec, Vec, type Obj } from './fields.js';

const fmt = (v: number[] | undefined) => (v ? v.map((n) => Math.round(n * 1000) / 1000).join(', ') : '0, 0, 0');

/** `{ center, radius }` spheres. */
export function SphereList({ title, hint, value, onChange, addLabel = 'Add sphere' }: { title: string; hint?: string; value: unknown; onChange: (v: Obj[] | undefined) => void; addLabel?: string }) {
  return (
    <ListEditor
      title={title}
      hint={hint}
      items={arr<unknown>(value).map((s) => obj(s) ?? {})}
      onChange={(v) => onChange(v.length ? v : undefined)}
      make={() => ({ center: [0, 0, 0], radius: 0.1 })}
      addLabel={addLabel}
      itemTitle={(s) => `(${fmt(vec(s.center, 3))}) r ${num(s.radius) ?? 0.1}`}
      render={(s, set) => (
        <>
          <Vec label="Center" hint="Sphere center (part space, m)." value={vec(s.center, 3)} onChange={(v) => set(patch(s, 'center', v))} />
          <Num label="Radius" hint="Sphere radius (m)." min={0} value={num(s.radius)} fallback={0.1} onChange={(v) => set(patch(s, 'radius', v))} />
        </>
      )}
    />
  );
}

/** `{ min, max }` boxes. */
export function BoxList({ title, hint, value, onChange }: { title: string; hint?: string; value: unknown; onChange: (v: Obj[] | undefined) => void }) {
  return (
    <ListEditor
      title={title}
      hint={hint}
      items={arr<unknown>(value).map((s) => obj(s) ?? {})}
      onChange={(v) => onChange(v.length ? v : undefined)}
      make={() => ({ min: [-0.1, -0.1, -0.1], max: [0.1, 0.1, 0.1] })}
      addLabel="Add box"
      itemTitle={(b) => `(${fmt(vec(b.min, 3))}) – (${fmt(vec(b.max, 3))})`}
      render={(b, set) => <BoxFields value={b} onChange={set} />}
    />
  );
}

function BoxFields({ value, onChange }: { value: Obj; onChange: (v: Obj) => void }) {
  return (
    <>
      <Vec label="Min" hint="Lower corner (part space, m)." value={vec(value.min, 3) ?? [-1, -1, -1]} onChange={(v) => onChange(patch(value, 'min', v))} />
      <Vec label="Max" hint="Upper corner (part space, m)." value={vec(value.max, 3) ?? [1, 1, 1]} onChange={(v) => onChange(patch(value, 'max', v))} />
    </>
  );
}

/** Region filters: every set filter must match. */
export function RegionFields({ value, onChange, bones }: { value: Obj; onChange: (v: Obj) => void; bones: string[] }) {
  const set = (key: string, v: unknown) => onChange(patch(value, key, v));
  return (
    <>
      <PickList
        label="Bones"
        hint='Skeleton joints (of a skin target) whose skin is covered, e.g. ["chest", "ribs", "spine"] for a dog vest.'
        options={bones}
        value={arr<string>(value.bones).length ? arr<string>(value.bones) : undefined}
        onChange={(v) => set('bones', v)}
      />
      <Num label="Above" hint="Only above this height (m, part space)." optional value={num(value.above)} onChange={(v) => set('above', v)} />
      <Num label="Below" hint="Only below this height (m, part space)." optional value={num(value.below)} onChange={(v) => set('below', v)} />
      <OptionalSection title="Box" hint="Only inside this box." value={obj(value.box)} make={() => ({ min: [-0.2, 0, -0.2], max: [0.2, 0.5, 0.2] })} onChange={(v) => set('box', v)}>
        {(b) => <BoxFields value={b} onChange={(v) => set('box', v)} />}
      </OptionalSection>
      <SphereList title="Spheres" hint="Only inside any of these spheres (or capsules)." value={value.spheres} onChange={(v) => set('spheres', v)} />
      <ListEditor
        title="Capsules"
        hint='Only inside any of these capsules (limbs: { "a": shoulder, "b": elbow, "radius": 0.08 } for an upper-arm plate). Combined with spheres: inside any sphere or capsule.'
        items={arr<unknown>(value.capsules).map((s) => obj(s) ?? {})}
        onChange={(v) => set('capsules', v.length ? v : undefined)}
        make={() => ({ a: [0, 0, 0], b: [0, 0.3, 0], radius: 0.08 })}
        addLabel="Add capsule"
        itemTitle={(c) => `(${fmt(vec(c.a, 3))}) → (${fmt(vec(c.b, 3))})`}
        render={(c, setC) => (
          <>
            <Vec label="A" hint="Capsule start (part space, m)." value={vec(c.a, 3)} onChange={(v) => setC(patch(c, 'a', v))} />
            <Vec label="B" hint="Capsule end (part space, m)." value={vec(c.b, 3) ?? [0, 1, 0]} onChange={(v) => setC(patch(c, 'b', v))} />
            <Num label="Radius" hint="Capsule radius (m)." min={0} value={num(c.radius)} fallback={0.1} onChange={(v) => setC(patch(c, 'radius', v))} />
          </>
        )}
      />
      <Vec
        label="Facing"
        hint="Only surface facing this way (e.g. [0, 1, 0]: the top of a back for a saddle)."
        optional
        fallback={[0, 1, 0]}
        value={vec(value.facing, 3)}
        onChange={(v) => set('facing', v)}
      />
      {vec(value.facing, 3) && (
        <Num
          label="Facing min"
          hint="Minimum cosine to facing (0 = side-on and up, 0.5 = mostly facing)."
          min={-1}
          max={1}
          step={0.05}
          value={num(value.facingMin)}
          fallback={0.2}
          onChange={(v) => set('facingMin', v)}
        />
      )}
      <Section title="Holes" open={Boolean(arr(value.exclude).length || arr(value.excludeBoxes).length)}>
        <SphereList title="Exclude spheres" hint="Holes: spheres cut out (neck, arms, tail, legs)." value={value.exclude} onChange={(v) => set('exclude', v)} />
        <BoxList title="Exclude boxes" hint="Holes: boxes cut out (a face below a hairline, a neckline, a back opening)." value={value.excludeBoxes} onChange={(v) => set('excludeBoxes', v)} />
        <Num
          label="Hole feather"
          hint="Soft edge width (m) of the sphere holes; unset = feather. Small = a crisp cut around an ear or an eye."
          optional
          min={0}
          step={0.001}
          value={num(value.excludeFeather)}
          onChange={(v) => set('excludeFeather', v)}
        />
      </Section>
      <Num label="Feather" hint="Soft edge width (m) of height / sphere filters." min={0} step={0.001} value={num(value.feather)} fallback={0.01} onChange={(v) => set('feather', v)} />
      <Num
        label="Outline"
        hint="Rounds the outline (m): the selection is smoothed over the surface before it is cut, so edges become clean curves (0 = exact; 0.01 typical; 0.03 very round)."
        min={0}
        step={0.001}
        value={num(value.outline)}
        fallback={0.008}
        onChange={(v) => set('outline', v)}
      />
    </>
  );
}

/** Short summary of a region for list titles. */
export function regionSummary(r: Obj): string {
  const parts: string[] = [];
  const bones = arr<string>(r.bones);
  if (bones.length) parts.push(bones.slice(0, 3).join(', ') + (bones.length > 3 ? '…' : ''));
  if (num(r.above) !== undefined) parts.push(`above ${num(r.above)}`);
  if (num(r.below) !== undefined) parts.push(`below ${num(r.below)}`);
  if (obj(r.box)) parts.push('box');
  if (arr(r.spheres).length) parts.push(`${arr(r.spheres).length} spheres`);
  if (arr(r.capsules).length) parts.push(`${arr(r.capsules).length} capsules`);
  return parts.join(' · ') || 'everywhere';
}
